import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/api/admin";
import type { Regola } from "@/types/admin";
import { tierLabel, PRECEDENZA } from "./tier";
import { RegolaForm } from "./RegolaForm";

const euro = (v: number) => `${v.toLocaleString("it-IT")} €`;

// Nome di ciò a cui la regola si applica; un'agenzia cancellata dal CRM arriva senza nome.
function bersaglio(r: Regola): string {
  const agenzia = r.agenzia_id ? (r.agenzia_name ?? "agenzia non più nel CRM") : null;
  return [agenzia, r.offerta_name, r.fornitore].filter(Boolean).join(" · ") || "tutte";
}

// Righe attive di un gradino, raggruppate per importo: "Acea, Segnoverde · 50 €".
function nelListino(regole: Regola[], tier: number): string[] {
  const attive = regole.filter((r) => r.attivo && r.tier === tier && r.valore != null);
  const raggruppa = (lista: Regola[], fmt: (v: number) => string) => {
    const perValore = new Map<number, string[]>();
    for (const r of lista) perValore.set(r.valore!, [...(perValore.get(r.valore!) ?? []), bersaglio(r)]);
    return [...perValore].map(([v, nomi]) => `${nomi.sort().join(", ")} · ${fmt(v)}`);
  };
  return [
    ...raggruppa(attive.filter((r) => r.metodo_pagamento !== "SDD"), euro),
    ...raggruppa(attive.filter((r) => r.metodo_pagamento === "SDD"), (v) => `con RID +${euro(v)}`),
  ];
}

// Esempio di precedenza coi dati veri: una regola d'agenzia (riga 3) e una di fornitore (riga 5).
function esempio(regole: Regola[]): string | null {
  const base = regole.filter((r) => r.attivo && r.metodo_pagamento !== "SDD" && r.valore != null);
  const agenzia = base.find((r) => r.tier === 3 && r.agenzia_name);
  const fornitore = base.find((r) => r.tier === 5 && r.valore !== agenzia?.valore);
  if (!agenzia || !fornitore) return null;
  return `Pratica ${fornitore.fornitore} di ${agenzia.agenzia_name}: corrisponde già alla riga 3, quindi ${euro(agenzia.valore!)}. La riga ${fornitore.fornitore} da ${euro(fornitore.valore!)} non si guarda.`;
}

export function Listino() {
  const [regole, setRegole] = useState<Regola[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingRegola, setEditingRegola] = useState<Regola | null>(null);

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState<Regola | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toggle-attivo confirm dialog (solo per la disattivazione)
  const [toggleTarget, setToggleTarget] = useState<Regola | null>(null);
  const [toggling, setToggling] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: "ok" | "err" } | null>(null);

  function showToast(msg: string, type: "ok" | "err") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminApi
      .listListino(includeInactive)
      .then(setRegole)
      .catch(() => setError("Impossibile caricare il listino."))
      .finally(() => setLoading(false));
  }, [includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditingRegola(null);
    setFormOpen(true);
  }

  function openEdit(r: Regola) {
    setEditingRegola(r);
    setFormOpen(true);
  }

  function onFormSaved() {
    load();
    showToast(editingRegola ? "Regola aggiornata" : "Regola creata", "ok");
  }

  // Riattivazione = immediata; disattivazione = chiede conferma (toglie dal calcolo).
  function requestToggle(r: Regola) {
    if (r.attivo) {
      setToggleTarget(r);
    } else {
      void doToggle(r);
    }
  }

  async function doToggle(r: Regola) {
    setToggling(true);
    try {
      await adminApi.toggleAttivo(r.id, !r.attivo);
      setToggleTarget(null);
      if (r.attivo) {
        // appena disattivata → tienila visibile (greyed) invece di farla sparire
        showToast("Regola disattivata", "ok");
        if (!includeInactive) setIncludeInactive(true);
        else load();
      } else {
        showToast("Regola riattivata", "ok");
        load();
      }
    } catch {
      showToast("Errore aggiornamento stato", "err");
    } finally {
      setToggling(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.deleteRegola(deleteTarget.id);
      setDeleteTarget(null);
      load();
      showToast("Regola eliminata", "ok");
    } catch {
      showToast("Errore eliminazione", "err");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Listino commissioni</h2>
          <p className="text-sm text-muted-foreground">
            Quanto spetta all'agenzia per ogni pratica attivata.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded border-border"
            />
            Mostra disattivate
          </label>
          <Button onClick={openCreate} size="sm">
            + Nuova regola
          </Button>
        </div>
      </div>

      {/* Scala delle righe — sempre visibile, con le regole attive di ogni riga */}
      <section
        aria-label="Ordine in cui si guardano le regole"
        className="rounded-lg border border-border bg-card overflow-hidden"
      >
        <p className="text-sm px-4 pt-3 pb-2.5">
          Per ogni pratica attivata il portale scorre le righe dall'alto e{" "}
          <strong>usa la prima che corrisponde</strong>; le altre non contano.
        </p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Riga</TableHead>
                <TableHead>Vale per</TableHead>
                <TableHead>Nel listino oggi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PRECEDENZA.map((p) => (
                <TableRow key={p.tier}>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className="text-xs tabular-nums font-semibold"
                    >
                      {p.tier}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium text-foreground whitespace-normal">
                    {p.titolo}
                  </TableCell>
                  <TableCell className="text-sm whitespace-normal">
                    {(() => {
                      if (loading || error) return <span className="text-muted-foreground">…</span>;
                      const righe = nelListino(regole, p.tier);
                      if (righe.length > 0)
                        return righe.map((t) => <div key={t}>{t}</div>);
                      return p.tier === 6 ? (
                        <span className="text-amber-700">
                          nessuna: senza una riga sopra, la pratica resta senza commissione
                        </span>
                      ) : (
                        <span className="text-muted-foreground">nessuna</span>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 py-3 border-t border-border space-y-2 text-sm">
          {!loading && esempio(regole) && (
            <p>
              <span className="font-medium">Esempio.</span> {esempio(regole)}
            </p>
          )}
          <p className="text-muted-foreground">
            Le righe <strong className="text-foreground">RID</strong> non sostituiscono la
            commissione: si aggiungono quando il cliente paga con domiciliazione.
          </p>
        </div>
      </section>

      {/* Content */}
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : regole.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessuna regola nel listino.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agenzia</TableHead>
                <TableHead>Offerta</TableHead>
                <TableHead>Fornitore</TableHead>
                <TableHead className="text-right">Commissione</TableHead>
                <TableHead className="text-right">Ricavo in più</TableHead>
                <TableHead>Riga</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {regole.map((r) => (
                <TableRow key={r.id} className={r.attivo ? "" : "opacity-60"}>
                  <TableCell className="text-foreground">
                    {r.agenzia_name ??
                      (r.agenzia_id ? (
                        <span className="text-amber-700">agenzia non più nel CRM</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      ))}
                  </TableCell>
                  <TableCell className="text-foreground">
                    {r.offerta_name ? (
                      <div className="flex flex-col gap-0.5">
                        <span>{r.offerta_name}</span>
                        {r.offerta_attiva === false && (
                          <Badge
                            variant="outline"
                            className="text-xs w-fit border-amber-400 text-amber-600"
                          >
                            offerta non attiva
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-foreground">
                    {r.fornitore ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium text-foreground">
                    <div className="flex items-center justify-end gap-1.5">
                      {r.metodo_pagamento === "SDD" && (
                        <Badge variant="outline" className="text-xs border-blue-400 text-blue-600">
                          RID
                        </Badge>
                      )}
                      {r.valore != null ? euro(r.valore) : <span className="text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-foreground">
                    {r.metodo_pagamento === "SDD" && r.valore_ricavo != null ? (
                      euro(r.valore_ricavo)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="secondary" className="text-xs">
                        {r.tier != null ? r.tier : "?"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {tierLabel(r.tier ?? null)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {/* Toggle attivo — plain checkbox styled as switch */}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={r.attivo}
                        onClick={() => requestToggle(r)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
                          r.attivo ? "bg-primary" : "bg-input"
                        }`}
                        title={r.attivo ? "Disattiva" : "Attiva"}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                            r.attivo ? "translate-x-4" : "translate-x-0"
                          }`}
                        />
                      </button>
                      {!r.attivo && (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          disattivata
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        title="Modifica"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(r)}
                        className="p-1.5 rounded hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive"
                        title="Elimina"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* RegolaForm dialog */}
      <RegolaForm
        open={formOpen}
        onOpenChange={setFormOpen}
        regola={editingRegola}
        onSaved={onFormSaved}
      />

      {/* Delete confirm dialog */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            className="bg-card text-foreground rounded-xl max-w-md w-full p-6 shadow-xl border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-2">Eliminare la regola?</h3>
            <p className="text-sm font-medium mb-3">
              {deleteTarget.name ||
                [
                  deleteTarget.agenzia_name,
                  deleteTarget.offerta_name,
                  deleteTarget.fornitore,
                ]
                  .filter(Boolean)
                  .join(" / ") ||
                `ID ${deleteTarget.id.slice(0, 8)}`}
            </p>
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2 text-sm text-destructive mb-5">
              <strong>Non si può annullare.</strong> Le commissioni già calcolate
              restano come sono, salvo un ricalcolo forzato. Per sospenderla
              soltanto, meglio disattivarla.
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={handleDelete}
                className="px-3 py-1.5 text-sm bg-destructive text-destructive-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {deleting ? "Eliminazione..." : "Elimina definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle-attivo (disattivazione) confirm dialog */}
      {toggleTarget && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !toggling && setToggleTarget(null)}
        >
          <div
            className="bg-card text-foreground rounded-xl max-w-md w-full p-6 shadow-xl border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-2">Disattivare la regola?</h3>
            <p className="text-sm font-medium mb-3">
              {toggleTarget.name ||
                [toggleTarget.agenzia_name, toggleTarget.offerta_name, toggleTarget.fornitore]
                  .filter(Boolean)
                  .join(" / ") ||
                `ID ${toggleTarget.id.slice(0, 8)}`}
            </p>
            <p className="text-sm text-muted-foreground mb-5">
              Le pratiche attivate da adesso <strong>non la useranno</strong>. Le
              commissioni già calcolate restano come sono, salvo un ricalcolo
              forzato. Si può riattivare quando serve.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={toggling}
                onClick={() => setToggleTarget(null)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="button"
                disabled={toggling}
                onClick={() => doToggle(toggleTarget)}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {toggling ? "Disattivazione..." : "Disattiva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-sm shadow-lg border ${
            toast.type === "ok"
              ? "bg-green-100 text-green-800 border-green-200"
              : "bg-red-100 text-red-800 border-red-200"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
